import { useState, useEffect, useCallback, useRef } from 'react';
import { Download, Calendar, Users, Route, Briefcase, TrendingUp, Clock, AlertTriangle, FileText, Fuel, Navigation } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Input';
import { getStaffPerformance, getDistanceReport, getJobsReport, getFuelCostReport, getDailyWork } from '../api/reports';
import { getFieldStaff } from '../api/users';

// ────────────────────────────────────
// Sabitler
// ────────────────────────────────────
const STATUS_LABELS = {
  pending: 'Bekliyor', assigned: 'Atandı', on_the_way: 'Yolda',
  started: 'Başladı', completed: 'Tamamlandı', cancelled: 'İptal',
};
const STATUS_COLORS = {
  pending: '#94a3b8', assigned: '#3b82f6', on_the_way: '#f97316',
  started: '#f59e0b', completed: '#22c55e', cancelled: '#ef4444',
};
const PRIORITY_LABELS = { urgent: 'Acil', normal: 'Normal', low: 'Düşük' };
const PRIORITY_COLORS = { urgent: '#ef4444', normal: '#3b82f6', low: '#94a3b8' };

const TABS = [
  { key: 'staff', label: 'Personel Performansı', icon: Users },
  { key: 'distance', label: 'KM / Rota', icon: Route },
  { key: 'jobs', label: 'İş Raporu', icon: Briefcase },
  { key: 'fuel', label: 'Yakıt & Maliyet', icon: Fuel },
  { key: 'mesai', label: 'Günlük Mesai', icon: Clock },
];

const PRESET_LABELS = {
  today: 'Bugün', week: 'Bu Hafta', month: 'Bu Ay', last30: 'Son 30 Gün', custom: 'Özel Aralık',
};

function dateRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'today': return { from: today.toISOString(), to: now.toISOString() };
    case 'week': {
      const d = new Date(today); d.setDate(d.getDate() - d.getDay() + 1);
      return { from: d.toISOString(), to: now.toISOString() };
    }
    case 'month': {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: d.toISOString(), to: now.toISOString() };
    }
    case 'last30': {
      const d = new Date(today); d.setDate(d.getDate() - 30);
      return { from: d.toISOString(), to: now.toISOString() };
    }
    default: return { from: today.toISOString(), to: now.toISOString() };
  }
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function fmtDuration(hours) {
  if (!hours && hours !== 0) return '—';
  const h = parseFloat(hours);
  if (isNaN(h) || h < 0) return '—';
  const totalMin = Math.round(h * 60);
  const hrs = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hrs === 0) return `${mins}dk`;
  return `${hrs}s ${mins}dk`;
}

// ────────────────────────────────────
// Yardımcı bileşenler
// ────────────────────────────────────
function Card({ children, className = '' }) {
  return <div className={`bg-white rounded-xl border border-slate-200 p-5 ${className}`}>{children}</div>;
}

function StatCard({ icon: Icon, label, value, sub, color = 'blue' }) {
  const bg = { blue: 'bg-blue-50 text-blue-600', green: 'bg-green-50 text-green-600', orange: 'bg-orange-50 text-orange-600', red: 'bg-red-50 text-red-600', purple: 'bg-purple-50 text-purple-600' };
  return (
    <Card>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${bg[color]}`}>
          <Icon size={18} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-800">{value ?? '—'}</p>
          <p className="text-xs text-slate-500">{label}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

function EmptyState({ text }) {
  return <div className="flex items-center justify-center h-40 text-slate-400 text-sm">{text}</div>;
}

// ────────────────────────────────────
// CSV export
// ────────────────────────────────────
function downloadCsv(filename, headers, rows) {
  const bom = '\uFEFF';
  const csv = bom + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ────────────────────────────────────
// PDF export
// ────────────────────────────────────
function addPdfHeader(doc, title, rangeLabel, staffName) {
  // Logo bar
  doc.setFillColor(30, 41, 59); // slate-800
  doc.rect(0, 0, 210, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Fieldpin', 14, 12);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(title, 14, 20);
  doc.text(rangeLabel, 196, 12, { align: 'right' });
  if (staffName) doc.text(staffName, 196, 20, { align: 'right' });
  doc.setTextColor(0, 0, 0);
}

async function exportPdf(contentRef, title, rangeLabel, staffName) {
  const el = contentRef.current;
  if (!el) return;

  const canvas = await html2canvas(el, {
    scale: 2, useCORS: true, backgroundColor: '#f8fafc',
    logging: false, windowWidth: el.scrollWidth,
  });

  const imgW = 182; // A4 width - margins
  const imgH = (canvas.height * imgW) / canvas.width;
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageH = 297;

  addPdfHeader(pdf, title, rangeLabel, staffName);

  let y = 32;
  const imgData = canvas.toDataURL('image/png');

  // Sayfa ekleme — uzun içerik birden fazla sayfaya bölünür
  if (imgH + y <= pageH - 10) {
    pdf.addImage(imgData, 'PNG', 14, y, imgW, imgH);
  } else {
    // Bölünmüş sayfa
    const pageContent = pageH - y - 10;
    let srcY = 0;
    let first = true;
    while (srcY < canvas.height) {
      if (!first) {
        pdf.addPage();
        y = 14;
      }
      const sliceH = first ? pageContent : pageH - 24;
      const srcSlice = (sliceH / imgW) * canvas.width;
      const drawH = Math.min(sliceH, ((canvas.height - srcY) * imgW) / canvas.width);

      // Clip canvas
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = Math.min(srcSlice, canvas.height - srcY);
      const ctx = sliceCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, srcY, canvas.width, sliceCanvas.height, 0, 0, canvas.width, sliceCanvas.height);

      pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', 14, y, imgW, drawH);
      srcY += srcSlice;
      first = false;
    }
  }

  const today = new Date().toISOString().slice(0, 10);
  pdf.save(`fieldpin-rapor-${today}.pdf`);
}

// Toplu PDF — her personel için tablo sayfası (jsPDF ile doğrudan)
function exportBulkStaffPdf(staffData, rangeLabel) {
  if (!staffData?.staff?.length) return;
  const pdf = new jsPDF('p', 'mm', 'a4');
  const staff = staffData.staff;

  // Kapak sayfası
  addPdfHeader(pdf, 'Tüm Personel Performans Raporu', rangeLabel, `${staff.length} personel`);
  pdf.setFontSize(11);
  let y = 40;

  // Özet tablosu
  const cols = ['Personel', 'Toplam', 'Tamamlanan', 'Geciken', 'KM', 'Tamamlama', 'Müşteride', 'Yolda'];
  const colW = [40, 16, 22, 18, 20, 22, 22, 22];
  const startX = 14;

  // Başlık satırı
  pdf.setFillColor(241, 245, 249); // slate-100
  pdf.rect(startX, y, 182, 8, 'F');
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(71, 85, 105);
  let x = startX + 2;
  cols.forEach((c, i) => { pdf.text(c, x, y + 5.5); x += colW[i]; });

  y += 10;
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(30, 41, 59);

  staff.forEach((s, idx) => {
    if (y > 270) { pdf.addPage(); y = 14; }
    if (idx % 2 === 0) {
      pdf.setFillColor(248, 250, 252);
      pdf.rect(startX, y - 1, 182, 7, 'F');
    }
    x = startX + 2;
    const row = [s.name, s.total, s.completed, s.overdue, s.total_km ? `${s.total_km}` : '—', fmtDuration(s.avg_completion_h), fmtDuration(s.avg_onsite_h), fmtDuration(s.avg_travel_h)];
    row.forEach((v, i) => { pdf.text(String(v ?? ''), x, y + 4); x += colW[i]; });
    y += 7;
  });

  // Her personel için detay sayfası
  staff.forEach(s => {
    pdf.addPage();
    addPdfHeader(pdf, 'Personel Detay Raporu', rangeLabel, s.name);

    pdf.setFontSize(10);
    pdf.setTextColor(30, 41, 59);
    let dy = 38;

    const metrics = [
      ['Toplam İş', s.total],
      ['Tamamlanan', s.completed],
      ['Geciken', s.overdue],
      ['İptal', s.cancelled],
      ['Toplam KM', s.total_km ? `${s.total_km} km` : 'Veri yok'],
      ['Ort. Tamamlama Süresi', fmtDuration(s.avg_completion_h)],
      ['Müşteride Geçirilen Süre', fmtDuration(s.avg_onsite_h)],
      ['Yolda Geçirilen Süre', fmtDuration(s.avg_travel_h)],
    ];

    metrics.forEach(([label, val]) => {
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 116, 139);
      pdf.text(label, 14, dy);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 41, 59);
      pdf.text(String(val ?? '—'), 90, dy);
      dy += 8;
    });

    // Tamamlanma oranı
    const rate = parseInt(s.total) > 0 ? Math.round((parseInt(s.completed) / parseInt(s.total)) * 100) : 0;
    dy += 4;
    pdf.setFillColor(241, 245, 249);
    pdf.roundedRect(14, dy, 90, 16, 3, 3, 'F');
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 116, 139);
    pdf.text('Tamamlanma Oranı', 18, dy + 6);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(34, 197, 94);
    pdf.text(`%${rate}`, 18, dy + 14);
  });

  const today = new Date().toISOString().slice(0, 10);
  pdf.save(`fieldpin-toplu-rapor-${today}.pdf`);
}

// ────────────────────────────────────
// TAB: Personel Performansı
// ────────────────────────────────────
function StaffTab({ data, staffFilter, rangeLabel }) {
  const contentRef = useRef(null);
  if (!data?.staff?.length) return <EmptyState text="Bu tarih aralığında veri yok" />;

  const allStaff = data.staff;
  const staff = staffFilter ? allStaff.filter(s => s.id === staffFilter) : allStaff;
  if (!staff.length) return <EmptyState text="Seçili personel için veri yok" />;

  const totalCompleted = staff.reduce((s, r) => s + (parseInt(r.completed) || 0), 0);
  const totalOverdue = staff.reduce((s, r) => s + (parseInt(r.overdue) || 0), 0);
  const avgCompletion = staff.filter(r => r.avg_completion_h).reduce((s, r, _, a) => s + parseFloat(r.avg_completion_h) / a.length, 0);

  const chartData = staff.map(s => ({
    name: s.name.split(' ')[0],
    Tamamlanan: parseInt(s.completed) || 0,
    Geciken: parseInt(s.overdue) || 0,
    İptal: parseInt(s.cancelled) || 0,
  }));

  const totalKm = staff.reduce((s, r) => s + (parseFloat(r.total_km) || 0), 0);

  const exportCsv = () => {
    downloadCsv('personel_performans.csv',
      ['Personel', 'Toplam', 'Tamamlanan', 'Geciken', 'İptal', 'Toplam KM', 'Ort. Tamamlama', 'Ort. Sahada', 'Ort. Yolda'],
      staff.map(s => [s.name, s.total, s.completed, s.overdue, s.cancelled, s.total_km ?? '', fmtDuration(s.avg_completion_h), fmtDuration(s.avg_onsite_h), fmtDuration(s.avg_travel_h)]),
    );
  };

  const staffName = staffFilter ? staff[0]?.name : null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 justify-end">
        <Button variant="ghost" size="sm" onClick={exportCsv}><Download size={14} /> CSV</Button>
        <Button variant="ghost" size="sm" onClick={() => exportPdf(contentRef, 'Personel Performans Raporu', rangeLabel, staffName)}>
          <FileText size={14} /> PDF
        </Button>
        {!staffFilter && (
          <Button variant="secondary" size="sm" onClick={() => exportBulkStaffPdf(data, rangeLabel)}>
            <FileText size={14} /> Tüm Personel Raporu
          </Button>
        )}
      </div>

      <div ref={contentRef} className="space-y-5">
        <div className="grid grid-cols-5 gap-4">
          <StatCard icon={Briefcase} label="Toplam Tamamlanan" value={totalCompleted} color="green" />
          <StatCard icon={AlertTriangle} label="Geciken İşler" value={totalOverdue} color="red" />
          <StatCard icon={Clock} label="Ort. Tamamlama" value={avgCompletion ? fmtDuration(avgCompletion) : '—'} color="blue" />
          <StatCard icon={Route} label="Toplam KM" value={totalKm ? `${Math.round(totalKm)} km` : '—'} color="orange" />
          <StatCard icon={TrendingUp} label="Aktif Personel" value={staff.length} color="purple" />
        </div>

        {chartData.length > 1 && (
          <Card>
            <h3 className="font-semibold text-slate-700 mb-4">Personel Karşılaştırması</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Tamamlanan" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Geciken" fill="#ef4444" radius={[4, 4, 0, 0]} />
                <Bar dataKey="İptal" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        <Card>
          <h3 className="font-semibold text-slate-700 mb-3">Detay Tablosu</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="pb-2 font-medium">Personel</th>
                  <th className="pb-2 font-medium text-center">Toplam</th>
                  <th className="pb-2 font-medium text-center">Tamamlanan</th>
                  <th className="pb-2 font-medium text-center">Geciken</th>
                  <th className="pb-2 font-medium text-center">İptal</th>
                  <th className="pb-2 font-medium text-center">Toplam KM</th>
                  <th className="pb-2 font-medium text-center">Ort. Tamamlama</th>
                  <th className="pb-2 font-medium text-center">Müşteride</th>
                  <th className="pb-2 font-medium text-center">Yolda</th>
                </tr>
              </thead>
              <tbody>
                {staff.map(s => (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="py-2.5 font-medium text-slate-800">{s.name}</td>
                    <td className="py-2.5 text-center">{s.total}</td>
                    <td className="py-2.5 text-center text-green-600 font-medium">{s.completed}</td>
                    <td className="py-2.5 text-center text-red-600 font-medium">{s.overdue}</td>
                    <td className="py-2.5 text-center text-slate-400">{s.cancelled}</td>
                    <td className="py-2.5 text-center text-blue-600 font-medium">{s.total_km ? `${s.total_km} km` : '—'}</td>
                    <td className="py-2.5 text-center">{fmtDuration(s.avg_completion_h)}</td>
                    <td className="py-2.5 text-center">{fmtDuration(s.avg_onsite_h)}</td>
                    <td className="py-2.5 text-center">{fmtDuration(s.avg_travel_h)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ────────────────────────────────────
// TAB: KM / Rota
// ────────────────────────────────────
function DistanceTab({ data, staffFilter, rangeLabel }) {
  const contentRef = useRef(null);
  if (!data?.totals?.length) return <EmptyState text="Bu tarih aralığında konum verisi yok" />;

  const allTotals = data.totals;
  const allDaily = data.daily;
  const totals = staffFilter ? allTotals.filter(t => t.user_id === staffFilter) : allTotals;
  const daily = staffFilter ? allDaily.filter(d => d.user_id === staffFilter) : allDaily;
  if (!totals.length) return <EmptyState text="Seçili personel için konum verisi yok" />;

  const totalKm = totals.reduce((s, t) => s + t.total_km, 0);

  const dayMap = {};
  daily.forEach(d => {
    const day = d.day?.slice(0, 10);
    if (!dayMap[day]) dayMap[day] = 0;
    dayMap[day] += parseFloat(d.distance_km || 0);
  });
  const trendData = Object.entries(dayMap).sort().map(([day, km]) => ({
    day: new Date(day).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }),
    km: Math.round(km * 10) / 10,
  }));

  const exportCsv = () => {
    downloadCsv('km_raporu.csv', ['Personel', 'Gün', 'KM'],
      daily.map(d => [d.name, d.day?.slice(0, 10), d.distance_km]),
    );
  };

  const staffName = staffFilter ? totals[0]?.name : null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 justify-end">
        <Button variant="ghost" size="sm" onClick={exportCsv}><Download size={14} /> CSV</Button>
        <Button variant="ghost" size="sm" onClick={() => exportPdf(contentRef, 'KM / Rota Raporu', rangeLabel, staffName)}>
          <FileText size={14} /> PDF
        </Button>
      </div>

      <div ref={contentRef} className="space-y-5">
        <div className="grid grid-cols-4 gap-4">
          <StatCard icon={Route} label="Toplam KM" value={`${Math.round(totalKm)} km`} color="blue" />
          <StatCard icon={Users} label="Aktif Personel" value={totals.length} color="purple" />
          <StatCard icon={TrendingUp} label="Ort. Günlük KM"
            value={trendData.length ? `${(totalKm / trendData.length).toFixed(1)} km` : '—'} color="green" />
          {totals[0] && (
            <StatCard icon={Navigation} label="En Çok KM"
              value={`${totals[0].total_km} km`}
              sub={totals[0].name} color="orange" />
          )}
        </div>

        {trendData.length > 0 && (
          <Card>
            <h3 className="font-semibold text-slate-700 mb-4">Günlük KM</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} unit=" km" />
                <Tooltip formatter={(v) => [`${v} km`, 'Toplam']} />
                <Bar dataKey="km" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {totals.length > 1 && (
          <Card>
            <h3 className="font-semibold text-slate-700 mb-3">Personel Bazlı KM</h3>
            <ResponsiveContainer width="100%" height={Math.max(180, totals.length * 40)}>
              <BarChart data={totals.map(t => ({ name: t.name.split(' ')[0], km: t.total_km }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" unit=" km" tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => [`${v} km`, 'Toplam']} />
                <Bar dataKey="km" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────
// TAB: İş Raporu
// ────────────────────────────────────
function JobsTab({ data, rangeLabel }) {
  const contentRef = useRef(null);
  if (!data) return <EmptyState text="Veri yükleniyor..." />;

  const { status_distribution, priority_distribution, customer_workload, avg_response_hours, daily_trend } = data;
  const totalJobs = status_distribution.reduce((s, r) => s + r.count, 0);
  const completedCount = (status_distribution.find(s => s.status === 'completed')?.count ?? 0) +
    (status_distribution.find(s => s.status === 'approved')?.count ?? 0);

  const statusData = status_distribution.map(s => ({
    name: STATUS_LABELS[s.status] || s.status,
    value: s.count,
    fill: STATUS_COLORS[s.status] || '#94a3b8',
  }));
  const priorityData = priority_distribution.map(p => ({
    name: PRIORITY_LABELS[p.priority] || p.priority,
    value: p.count,
    fill: PRIORITY_COLORS[p.priority] || '#94a3b8',
  }));
  const trendData = (daily_trend || []).map(d => ({
    day: new Date(d.day).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }),
    count: d.count,
  }));

  const exportCsv = () => {
    downloadCsv('is_raporu.csv', ['Durum', 'Adet'],
      status_distribution.map(s => [STATUS_LABELS[s.status] || s.status, s.count]),
    );
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 justify-end">
        <Button variant="ghost" size="sm" onClick={exportCsv}><Download size={14} /> CSV</Button>
        <Button variant="ghost" size="sm" onClick={() => exportPdf(contentRef, 'İş Raporu', rangeLabel)}>
          <FileText size={14} /> PDF
        </Button>
      </div>

      <div ref={contentRef} className="space-y-5">
        <div className="grid grid-cols-4 gap-4">
          <StatCard icon={Briefcase} label="Toplam İş" value={totalJobs} color="blue" />
          <StatCard icon={TrendingUp} label="Tamamlanan" value={completedCount} color="green" />
          <StatCard icon={Clock} label="Ort. Yanıt Süresi"
            value={avg_response_hours ? `${avg_response_hours} sa` : '—'}
            sub="Oluşturulma → Atama" color="orange" />
          <StatCard icon={AlertTriangle} label="Tamamlanma Oranı"
            value={totalJobs ? `%${Math.round(completedCount / totalJobs * 100)}` : '—'} color="purple" />
        </div>

        <div className="grid grid-cols-2 gap-5">
          <Card>
            <h3 className="font-semibold text-slate-700 mb-4">Durum Dağılımı</h3>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={90}
                    dataKey="value" nameKey="name"
                    label={({ name, percent }) => `${name} %${(percent * 100).toFixed(0)}`}
                    labelLine={{ strokeWidth: 1 }}>
                    {statusData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyState text="Veri yok" />}
          </Card>

          <Card>
            <h3 className="font-semibold text-slate-700 mb-4">Öncelik Dağılımı</h3>
            {priorityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={priorityData} cx="50%" cy="50%" innerRadius={50} outerRadius={90}
                    dataKey="value" nameKey="name"
                    label={({ name, percent }) => `${name} %${(percent * 100).toFixed(0)}`}
                    labelLine={{ strokeWidth: 1 }}>
                    {priorityData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyState text="Veri yok" />}
          </Card>
        </div>

        {trendData.length > 0 && (
          <Card>
            <h3 className="font-semibold text-slate-700 mb-4">Günlük İş Oluşturma Trendi</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} name="İş Sayısı" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {customer_workload?.length > 0 && (
          <Card>
            <h3 className="font-semibold text-slate-700 mb-3">Müşteri Bazlı İş Yoğunluğu (İlk 10)</h3>
            <ResponsiveContainer width="100%" height={Math.max(200, customer_workload.length * 36)}>
              <BarChart data={customer_workload.map(c => ({ name: c.name, count: c.job_count }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" name="İş Sayısı" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────
// TAB: Yakıt & Maliyet
// ────────────────────────────────────
const FUEL_TYPE_LABELS = { benzin: 'Benzin', dizel: 'Dizel', elektrik: 'Elektrik', hybrid: 'Hybrid' };
const FUEL_COLORS = { benzin: '#f59e0b', dizel: '#6366f1', elektrik: '#22c55e', hybrid: '#0ea5e9' };

function FuelTab({ data, staffFilter, rangeLabel }) {
  const contentRef = useRef(null);
  if (!data) return <EmptyState text="Veri yükleniyor..." />;

  const { vehicle_costs, staff_costs: allStaffCosts, fuel_prices, monthly_summary } = data;

  const staffCosts = staffFilter ? allStaffCosts.filter(s => s.user_id === staffFilter) : allStaffCosts;

  const totalVehicleCost = vehicle_costs.reduce((s, v) => s + (v.total_cost || 0), 0);
  const totalEstimated = staffCosts.reduce((s, r) => s + (r.estimated_cost || 0), 0);
  const totalKm = staffCosts.reduce((s, r) => s + (r.total_km || 0), 0);

  // Aylık trend
  const monthlyData = {};
  monthly_summary.forEach(m => {
    if (!monthlyData[m.month]) monthlyData[m.month] = { month: m.month, cost: 0 };
    monthlyData[m.month].cost += m.cost;
  });
  const trendData = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month)).map(m => ({
    month: m.month,
    cost: Math.round(m.cost),
  }));

  const exportCsv = () => {
    downloadCsv('yakit_maliyet.csv',
      ['Personel', 'KM', 'Araç', 'Yakıt Tipi', 'Tüketim', 'Tahmini Maliyet (TL)'],
      staffCosts.map(s => [s.name, s.total_km, s.plate ?? '—', FUEL_TYPE_LABELS[s.fuel_type] ?? '—', s.fuel_consumption ?? '—', s.estimated_cost ?? '—']),
    );
  };

  const staffName = staffFilter ? staffCosts[0]?.name : null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 justify-end">
        <Button variant="ghost" size="sm" onClick={exportCsv}><Download size={14} /> CSV</Button>
        <Button variant="ghost" size="sm" onClick={() => exportPdf(contentRef, 'Yakıt & Maliyet Raporu', rangeLabel, staffName)}>
          <FileText size={14} /> PDF
        </Button>
      </div>

      <div ref={contentRef} className="space-y-5">
        {/* Özet kartlar */}
        <div className="grid grid-cols-4 gap-4">
          <StatCard icon={Fuel} label="Gerçek Yakıt Gideri" value={totalVehicleCost ? `${Math.round(totalVehicleCost).toLocaleString('tr-TR')} TL` : '—'} color="orange" />
          <StatCard icon={TrendingUp} label="Tahmini Maliyet (KM)" value={totalEstimated ? `${Math.round(totalEstimated).toLocaleString('tr-TR')} TL` : '—'} sub="KM × Tüketim × Fiyat" color="blue" />
          <StatCard icon={Route} label="Toplam KM" value={totalKm ? `${Math.round(totalKm).toLocaleString('tr-TR')} km` : '—'} color="green" />
          <StatCard icon={Fuel} label="Kayıtlı Araç" value={vehicle_costs.length} color="purple" />
        </div>

        {/* Güncel fiyatlar */}
        {Object.keys(fuel_prices).length > 0 && (
          <Card>
            <h3 className="font-semibold text-slate-700 mb-3">Güncel Yakıt Fiyatları</h3>
            <div className="flex gap-4">
              {Object.entries(fuel_prices).map(([type, info]) => (
                <div key={type} className="bg-slate-50 rounded-lg px-4 py-2 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: FUEL_COLORS[type] }} />
                  <span className="text-sm font-medium">{FUEL_TYPE_LABELS[type]}</span>
                  <span className="text-sm text-slate-600">{parseFloat(info.price).toFixed(2)} {info.unit}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Personel bazlı maliyet */}
        {staffCosts.length > 0 && (
          <Card>
            <h3 className="font-semibold text-slate-700 mb-3">Personel Bazlı Tahmini Yakıt Maliyeti</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="pb-2 font-medium">Personel</th>
                    <th className="pb-2 font-medium text-right">KM</th>
                    <th className="pb-2 font-medium">Araç</th>
                    <th className="pb-2 font-medium">Yakıt</th>
                    <th className="pb-2 font-medium text-right">Tüketim</th>
                    <th className="pb-2 font-medium text-right">Fiyat</th>
                    <th className="pb-2 font-medium text-right">Tahmini Maliyet</th>
                  </tr>
                </thead>
                <tbody>
                  {staffCosts.map((s, i) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2.5 font-medium text-slate-800">{s.name}</td>
                      <td className="py-2.5 text-right">{s.total_km ? `${s.total_km} km` : '—'}</td>
                      <td className="py-2.5 text-slate-600">{s.plate ?? '—'}</td>
                      <td className="py-2.5">
                        {s.fuel_type ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: `${FUEL_COLORS[s.fuel_type]}20`, color: FUEL_COLORS[s.fuel_type] }}>
                            {FUEL_TYPE_LABELS[s.fuel_type]}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="py-2.5 text-right text-slate-500">{s.fuel_consumption ?? '—'}</td>
                      <td className="py-2.5 text-right text-slate-500">{s.fuel_price ? `${parseFloat(s.fuel_price).toFixed(2)} TL` : '—'}</td>
                      <td className="py-2.5 text-right font-medium text-orange-600">
                        {s.estimated_cost ? `${s.estimated_cost.toLocaleString('tr-TR')} TL` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Araç bazlı gerçek yakıt gideri */}
        {vehicle_costs.some(v => v.total_cost > 0) && (
          <Card>
            <h3 className="font-semibold text-slate-700 mb-3">Araç Bazlı Yakıt Gideri (Log Kayıtları)</h3>
            <ResponsiveContainer width="100%" height={Math.max(200, vehicle_costs.filter(v => v.total_cost > 0).length * 40)}>
              <BarChart data={vehicle_costs.filter(v => v.total_cost > 0).map(v => ({
                name: v.plate,
                cost: Math.round(v.total_cost),
              }))} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [`${v.toLocaleString('tr-TR')} TL`, 'Yakıt Gideri']} />
                <Bar dataKey="cost" fill="#f59e0b" name="Yakıt Gideri (TL)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Aylık trend */}
        {trendData.length > 0 && (
          <Card>
            <h3 className="font-semibold text-slate-700 mb-4">Aylık Yakıt Gideri Trendi</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => [`${v.toLocaleString('tr-TR')} TL`, 'Gider']} />
                <Bar dataKey="cost" fill="#6366f1" name="Toplam (TL)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────
// Günlük Mesai tab (Sprint 2.10 GÖREV 3) — self-contained: TEK tarih + getDailyWork
// ────────────────────────────────────
export function MesaiTab({ staffId, staffList }) {
  const [date, setDate] = useState(() => new Date().toLocaleDateString('en-CA')); // yerel bugün YYYY-MM-DD
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!staffId) { setData(null); return undefined; }
    let alive = true;
    setLoading(true); setError('');
    getDailyWork({ user_id: staffId, date })
      .then(d => { if (alive) setData(d.report); })
      .catch(() => { if (alive) setError('Rapor yüklenemedi'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [staffId, date]);

  const fmtDur = (sec) => (sec == null ? null : (sec < 3600 ? `${Math.round(sec / 60)} dk` : `${(sec / 3600).toFixed(1)} sa`));
  const fmtH = (h) => (h == null ? null : `${Number(h).toFixed(1)} sa`);
  const fmtKm = (km) => (km == null ? null : `${Number(km).toFixed(1)} km`);
  const fmtTime = (ts) => (ts ? new Date(ts).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : null);
  const staffName = staffList.find(s => s.id === staffId)?.name;

  return (
    <div className="space-y-5">
      {/* TEK tarih kontrolü — NET (aralık değil) */}
      <div className="flex items-center gap-2">
        <Calendar size={15} className="text-slate-400" />
        <span className="text-sm text-slate-500">Tarih (tek gün):</span>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
      </div>

      {!staffId ? (
        <EmptyState text="Günlük mesai raporu için yukarıdan bir personel seçin." />
      ) : loading ? (
        <EmptyState text="Yükleniyor…" />
      ) : error ? (
        <EmptyState text={error} />
      ) : !data ? (
        <EmptyState text="Veri yok." />
      ) : (
        <>
          {!data.shift_start_at && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
              {staffName || 'Personel'} bu gün mesai başlatmamış (work_session yok) — KPI'lar "—" gösterir.
            </div>
          )}
          <div className="grid grid-cols-4 gap-4">
            <StatCard icon={Clock}      label="Mesai Başı"       value={fmtTime(data.shift_start_at)}            color="blue" />
            <StatCard icon={Clock}      label="Evde Bekleme"     value={fmtDur(data.wait_before_departure_sec)} color="orange" />
            <StatCard icon={Navigation} label="İlk Varış"        value={fmtDur(data.first_arrival_sec)}         color="purple" />
            <StatCard icon={Route}      label="Toplam KM"        value={fmtKm(data.total_km)}                   color="green" />
            <StatCard icon={TrendingUp} label="Toplam Yolda"     value={fmtH(data.total_travel_h)}              color="orange" />
            <StatCard icon={Clock}      label="Toplam Müşteride" value={fmtH(data.total_onsite_h)}              color="blue" />
            <StatCard icon={Clock}      label="Ofise Dönüş"      value={fmtTime(data.office_return_at)}         color="green" />
            <StatCard icon={Route}      label="Ofise Dönüş KM"   value={fmtKm(data.return_km)}
              sub={data.return_duration_min != null ? `${data.return_duration_min} dk` : null} color="purple" />
          </div>

          <Card>
            <h3 className="font-semibold text-slate-700 mb-3">O Günkü İşler ({data.jobs?.length || 0})</h3>
            {(!data.jobs || data.jobs.length === 0) ? (
              <EmptyState text="Bu gün iş kaydı yok." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="pb-2 font-medium">İş</th>
                      <th className="pb-2 font-medium">Durum</th>
                      <th className="pb-2 font-medium text-center">Yolda (sa)</th>
                      <th className="pb-2 font-medium text-center">Müşteride (sa)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.jobs.map(j => (
                      <tr key={j.id} className="border-b border-slate-50">
                        <td className="py-2">{j.title}</td>
                        <td className="py-2">{STATUS_LABELS[j.status] || j.status}</td>
                        <td className="py-2 text-center">{j.travel_h != null ? Number(j.travel_h).toFixed(1) : '—'}</td>
                        <td className="py-2 text-center">{j.onsite_h != null ? Number(j.onsite_h).toFixed(1) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {data.metrics_note && <p className="text-xs text-slate-400">ℹ {data.metrics_note}</p>}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────
// ANA SAYFA
// ────────────────────────────────────
export function ReportsPage() {
  const [tab, setTab] = useState('staff');
  const [preset, setPreset] = useState('last30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [staffData, setStaffData] = useState(null);
  const [distData, setDistData] = useState(null);
  const [jobsData, setJobsData] = useState(null);
  const [fuelData, setFuelData] = useState(null);

  // Personel listesini yükle
  useEffect(() => {
    getFieldStaff().then(d => setStaffList(d.users)).catch(() => {});
  }, []);

  const getRange = useCallback(() => {
    if (preset === 'custom' && customFrom && customTo) {
      return { from: new Date(customFrom).toISOString(), to: new Date(customTo + 'T23:59:59').toISOString() };
    }
    return dateRange(preset);
  }, [preset, customFrom, customTo]);

  const rangeLabel = (() => {
    const r = getRange();
    return `${fmtDate(r.from)} — ${fmtDate(r.to)}`;
  })();

  const load = useCallback(async () => {
    setLoading(true);
    const range = getRange();
    try {
      if (tab === 'staff') {
        setStaffData(await getStaffPerformance(range));
      } else if (tab === 'distance') {
        setDistData(await getDistanceReport(range));
      } else if (tab === 'fuel') {
        setFuelData(await getFuelCostReport(range));
      } else if (tab === 'jobs') {
        setJobsData(await getJobsReport(range));
      }
      // tab === 'mesai' → MesaiTab kendi verisini çeker (load no-op)
    } catch { }
    setLoading(false);
  }, [tab, getRange]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Başlık + Filtreler */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="mr-auto">
          <h1 className="text-2xl font-bold text-slate-800">Raporlar</h1>
          <p className="text-slate-500 text-sm mt-0.5">Performans, mesafe ve iş analizleri</p>
        </div>

        {/* Personel filtresi */}
        <div className="flex items-center gap-2">
          <Users size={15} className="text-slate-400" />
          <Select value={staffFilter} onChange={e => setStaffFilter(e.target.value)}>
            <option value="">Tüm Personel</option>
            {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </div>

        {/* Tarih filtresi — Mesai tab'ında gizli (MesaiTab kendi TEK tarihini kullanır) */}
        {tab !== 'mesai' && (
          <>
            <div className="flex items-center gap-2">
              <Calendar size={15} className="text-slate-400" />
              <Select value={preset} onChange={e => setPreset(e.target.value)}>
                <option value="today">Bugün</option>
                <option value="week">Bu Hafta</option>
                <option value="month">Bu Ay</option>
                <option value="last30">Son 30 Gün</option>
                <option value="custom">Özel Aralık</option>
              </Select>
            </div>

            {preset === 'custom' && (
              <div className="flex items-center gap-2">
                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <span className="text-slate-400">—</span>
                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Tab'lar */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-6 w-fit">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition
              ${tab === key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* İçerik */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {tab === 'staff' && <StaffTab data={staffData} staffFilter={staffFilter} rangeLabel={rangeLabel} />}
          {tab === 'distance' && <DistanceTab data={distData} staffFilter={staffFilter} rangeLabel={rangeLabel} />}
          {tab === 'jobs' && <JobsTab data={jobsData} rangeLabel={rangeLabel} />}
          {tab === 'fuel' && <FuelTab data={fuelData} staffFilter={staffFilter} rangeLabel={rangeLabel} />}
          {tab === 'mesai' && <MesaiTab staffId={staffFilter} staffList={staffList} />}
        </>
      )}
    </div>
  );
}
